import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

// Menu labels are endonyms on purpose: someone who can't read English must
// still be able to recognize their own language in the list.
// Somali is feature-flagged off for launch (translations are also still
// partial — see BUGS.md #003). Set REACT_APP_ENABLE_SOMALI=true to show it.
const ENABLE_SOMALI = process.env.REACT_APP_ENABLE_SOMALI === "true";
const LANGS = [
  { code: "en", short: "EN", label: "English" },
  ...(ENABLE_SOMALI ? [{ code: "so", short: "SO", label: "Soomaali" }] : []),
  { code: "fr", short: "FR", label: "Français" },
] as const;

// Language dropdown. Lives in the navbar so it's reachable from every screen,
// logged in or out — a not-yet-user following a WhatsApp invite link should be
// able to switch before they've seen anything else. Persists via
// i18next-browser-languagedetector (localStorage), not tied to auth state.
const LanguageToggle = () => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const current =
    LANGS.find((l) => i18n.language?.startsWith(l.code)) ?? LANGS[0];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setAnchorEl(null);
  };

  return (
    <Box>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Language: ${current.label}`}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.6,
          borderRadius: "8px",
          bgcolor: alpha(theme.palette.text.secondary, 0.08),
          color: "text.secondary",
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.02em",
          "&:hover": { bgcolor: alpha(theme.palette.text.secondary, 0.14) },
        }}
      >
        <LanguageIcon sx={{ fontSize: "1rem" }} />
        {current.short}
        <ExpandMoreIcon
          sx={{
            fontSize: "0.9rem",
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5 } } }}
      >
        {LANGS.map((lang) => {
          const active = current.code === lang.code;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={active}
              sx={{ fontSize: "0.9rem", py: 1 }}
            >
              <ListItemText
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: active ? 650 : 450,
                }}
              >
                {lang.label}
              </ListItemText>
              {active && (
                <ListItemIcon sx={{ minWidth: 0, ml: 1.5 }}>
                  <CheckIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LanguageToggle;
